/**
 * Page de gestion d'abonnement avec Stripe
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  CreditCard,
  Receipt,
  Warning,
  Info,
  LocalOffer,
  Star,
  ArrowForward,
} from '@mui/icons-material';
import { clientSubscriptionApi, Subscription } from '../../services/clientApi';

interface Payment {
  id: number;
  amount: number;
  currency: string;
  status: string;
  payment_date: string;
  stripe_invoice_id?: string;
}

export default function ClientSubscription() {
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [subscriptionRes, paymentsRes] = await Promise.all([
        clientSubscriptionApi.getCurrent(),
        clientSubscriptionApi.getPayments(),
      ]);

      setSubscription(subscriptionRes.data.subscription);
      setPayments(paymentsRes.data.payments || []);
    } catch (err: any) {
      console.error('Erreur chargement abonnement:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      setProcessing(true);
      const response = await clientSubscriptionApi.createCheckoutSession();

      // Rediriger vers Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err: any) {
      console.error('Erreur création checkout:', err);
      alert(err.response?.data?.message || 'Erreur lors de la création de la session de paiement');
      setProcessing(false);
    }
  };

  const handleBillingPortal = async () => {
    try {
      setProcessing(true);
      const response = await clientSubscriptionApi.getBillingPortal();

      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (err: any) {
      console.error('Erreur portail facturation:', err);
      alert(err.response?.data?.message || 'Erreur lors de l\'accès au portail');
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setProcessing(true);
      await clientSubscriptionApi.cancel();
      await fetchSubscriptionData();
      setCancelDialogOpen(false);
      alert('Votre abonnement a été annulé. Il restera actif jusqu\'à la fin de la période payée.');
    } catch (err: any) {
      console.error('Erreur annulation:', err);
      alert(err.response?.data?.message || 'Erreur lors de l\'annulation');
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setProcessing(true);
      await clientSubscriptionApi.reactivate();
      await fetchSubscriptionData();
      alert('Votre abonnement a été réactivé avec succès !');
    } catch (err: any) {
      console.error('Erreur réactivation:', err);
      alert(err.response?.data?.message || 'Erreur lors de la réactivation');
    } finally {
      setProcessing(false);
    }
  };

  const daysRemaining = (date: string) => {
    return Math.ceil((new Date(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  const formatAmount = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
          Chargement de votre abonnement...
        </Typography>
      </Container>
    );
  }

  if (error || !subscription) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Abonnement introuvable'}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/client/dashboard')}>
          Retour au dashboard
        </Button>
      </Container>
    );
  }

  const isTrial = subscription.is_trial;
  const isActive = subscription.status === 'active';
  const isCancelled = subscription.status === 'cancelled';
  const trialDaysLeft = isTrial && subscription.trial_ends_at ? daysRemaining(subscription.trial_ends_at) : 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/client/dashboard')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ ml: 2 }}>
          Gestion de l'abonnement
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Statut de l'abonnement */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" gutterBottom>
                    {isTrial ? '🎉 Période d\'essai gratuite' : 'Abonnement Premium'}
                  </Typography>

                  {isTrial ? (
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        icon={<Star />}
                        label={`${trialDaysLeft} jours restants`}
                        color={trialDaysLeft < 30 ? 'warning' : 'success'}
                        sx={{ mb: 2 }}
                      />
                      <Typography variant="body1" paragraph>
                        Profitez gratuitement de toutes les fonctionnalités jusqu'au{' '}
                        <strong>
                          {new Date(subscription.trial_ends_at!).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </strong>
                      </Typography>

                      {trialDaysLeft < 30 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Votre période d'essai se termine bientôt. Passez à l'abonnement payant pour continuer à profiter du service.
                        </Alert>
                      )}

                      <Typography variant="h6" color="primary" gutterBottom>
                        Après l'essai : 15€/mois
                      </Typography>

                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary="Synchronisation automatique 3x/jour" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary="Notifications push en temps réel" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary="Historique complet des événements" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary="Synthèse vocale multilingue" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary="Support prioritaire" />
                        </ListItem>
                      </List>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        icon={isActive ? <CheckCircle /> : <Warning />}
                        label={isActive ? 'Actif' : isCancelled ? 'Annulé' : subscription.status}
                        color={isActive ? 'success' : 'warning'}
                        sx={{ mb: 2 }}
                      />
                      <Typography variant="body1" paragraph>
                        Montant : <strong>{formatAmount(subscription.price_amount || 15)}</strong> par mois
                      </Typography>
                      {subscription.next_billing_date && (
                        <Typography variant="body2" color="text.secondary">
                          Prochain paiement le{' '}
                          {new Date(subscription.next_billing_date).toLocaleDateString('fr-FR')}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {isTrial ? (
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={handleUpgrade}
                      disabled={processing}
                    >
                      Passer à l'abonnement payant
                    </Button>
                  ) : (
                    <>
                      {!isCancelled && (
                        <>
                          <Button
                            variant="outlined"
                            startIcon={<CreditCard />}
                            onClick={handleBillingPortal}
                            disabled={processing}
                          >
                            Gérer mon paiement
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={() => setCancelDialogOpen(true)}
                            disabled={processing}
                          >
                            Annuler l'abonnement
                          </Button>
                        </>
                      )}
                      {isCancelled && (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={handleReactivate}
                          disabled={processing}
                        >
                          Réactiver l'abonnement
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Historique des paiements */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Historique des paiements
              </Typography>
              <Divider sx={{ my: 2 }} />

              {payments.length === 0 ? (
                <Alert severity="info" icon={<Info />}>
                  Aucun paiement pour le moment.
                  {isTrial && ' Vous êtes actuellement en période d\'essai gratuite.'}
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Montant</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Facture</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>
                            {new Date(payment.payment_date).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell>{formatAmount(payment.amount, payment.currency)}</TableCell>
                          <TableCell>
                            <Chip
                              label={payment.status === 'succeeded' ? 'Payé' : payment.status}
                              color={payment.status === 'succeeded' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {payment.stripe_invoice_id && (
                              <Button
                                size="small"
                                startIcon={<Receipt />}
                                href={`https://dashboard.stripe.com/invoices/${payment.stripe_invoice_id}`}
                                target="_blank"
                              >
                                Voir
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Avantages de l'abonnement */}
        {!isTrial && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Vos avantages Premium
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle color="success" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="medium">
                          Synchronisation automatique
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Vos dossiers sont mis à jour automatiquement 3 fois par jour
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle color="success" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="medium">
                          Notifications push
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Recevez des alertes en temps réel sur l'état de vos dossiers
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle color="success" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="medium">
                          Synthèse vocale
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Écoutez l'état de vos dossiers dans votre langue
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle color="success" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="medium">
                          Support prioritaire
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Une équipe dédiée pour répondre à vos questions
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Dialog de confirmation d'annulation */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Annuler votre abonnement ?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Si vous annulez votre abonnement :
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Votre accès restera actif jusqu'à la fin de la période payée</li>
              <li>Vous ne serez plus débité automatiquement</li>
              <li>Vous pourrez réactiver votre abonnement à tout moment</li>
            </ul>
          </Alert>
          <Typography variant="body2">
            Êtes-vous sûr de vouloir annuler votre abonnement ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Garder mon abonnement</Button>
          <Button onClick={handleCancelSubscription} color="error" disabled={processing}>
            Oui, annuler
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
